/* ss-quick-add-feedback.js
   Hybrid quick-add confirmation (Sho, 2026-09-12). The PDP add-to-cart form
   keeps opening the full drawer exactly as before — untouched. Every OTHER
   add control (browse cards, collection grid, mega menu, mobile shop nav,
   homepage rails, the browse quiz) now fires a `quiet` add: the drawer stays
   shut, and this module paints the "over the cart" confirmation instead —
   a small thumbnail arcing to the header cart icon, a soft pulse on the
   icon, and a ~2s window where re-tapping the just-added control opens the
   drawer (the same window sections/*.liquid label "Added · View cart"
   through their own button-state code; this file only owns the motion and
   the re-tap).

   Wired from `sacrasoul:quick-add`, dispatched by addAndOpenAuthoritative in
   sacrasoul-cart-drawer.js (and queued the same way from the pre-boot shim
   in sacrasoul-cart-boot.js) whenever the caller passed `{ quiet: true }`.
   Nothing here decides whether an add is quiet — it only reacts once one
   has already happened. Errors (sold out, rejected) never reach this file:
   they are today's button-level retry/sold-out states, untouched. */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var REOPEN_WINDOW_MS = 2000;
  var MARK_CLASS = 'ss-qaf-added';

  function pulseCartIcon() {
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    icon.classList.remove('ss-qaf-pulse');
    /* force a reflow so retriggering the class restarts the CSS animation
       on a rapid second add */
    void icon.offsetWidth;
    icon.classList.add('ss-qaf-pulse');
    setTimeout(function () { icon.classList.remove('ss-qaf-pulse'); }, 500);
  }

  function flyToCart(origin) {
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon || !origin || !origin.getBoundingClientRect) { pulseCartIcon(); return; }

    var card = (origin.closest && (
      origin.closest('[data-product-image]') ||
      origin.closest('.ssb-card, .ssb-oilset-card, .ssb-set-card, .hp-plate, .ssm-cell, .msc-card')
    )) || origin;
    var imgEl = card.querySelector ? card.querySelector('img') : null;
    var originRect = (imgEl || origin).getBoundingClientRect();
    var targetRect = icon.getBoundingClientRect();
    if (!originRect.width || !targetRect.width) { pulseCartIcon(); return; }

    var size = 34;
    var dot = document.createElement('div');
    dot.className = 'ss-qaf-fly';
    if (imgEl && (imgEl.currentSrc || imgEl.src)) {
      var img = document.createElement('img');
      img.className = 'ss-qaf-fly__img';
      img.alt = '';
      img.src = imgEl.currentSrc || imgEl.src;
      dot.appendChild(img);
    }
    document.body.appendChild(dot);

    var startX = originRect.left + originRect.width / 2 - size / 2;
    var startY = originRect.top + originRect.height / 2 - size / 2;
    var endX = targetRect.left + targetRect.width / 2 - size / 2;
    var endY = targetRect.top + targetRect.height / 2 - size / 2;
    var midX = (startX + endX) / 2;
    var midY = Math.min(startY, endY) - 56;

    var frames = [
      { transform: 'translate(' + startX + 'px,' + startY + 'px) scale(1)', opacity: 1, offset: 0 },
      { transform: 'translate(' + midX + 'px,' + midY + 'px) scale(0.82)', opacity: 1, offset: 0.55 },
      { transform: 'translate(' + endX + 'px,' + endY + 'px) scale(0.28)', opacity: 0, offset: 1 }
    ];

    var finish = function () {
      if (dot.parentNode) dot.parentNode.removeChild(dot);
      pulseCartIcon();
    };

    if (typeof dot.animate === 'function') {
      var anim = dot.animate(frames, { duration: 380, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' });
      anim.onfinish = finish;
      /* a hidden tab (rAF frozen) never fires onfinish; do not leave the dot forever */
      setTimeout(finish, 900);
    } else {
      /* no WAAPI: skip the flight, still confirm with the pulse */
      finish();
    }
  }

  function markReopenable(origin) {
    if (!origin || !origin.classList) return;
    origin.classList.add(MARK_CLASS);
    if (origin._ssQafTimer) clearTimeout(origin._ssQafTimer);
    origin._ssQafTimer = setTimeout(function () {
      origin.classList.remove(MARK_CLASS);
      origin._ssQafTimer = null;
    }, REOPEN_WINDOW_MS);
  }

  /* The sheet loads with the media="print" swap (sections/header.liquid), so
     an add fired in the first moments of a slow load can arrive before the
     rules that position the flying thumbnail exist. Without them the dot
     would land in document flow as an unstyled image. Confirm silently in
     that window (the badge number is a DOM text change and still updates);
     the flight and pulse return the moment the sheet has applied. */
  function sheetReady() {
    var sheets = document.styleSheets;
    for (var i = 0; i < sheets.length; i++) {
      var href = sheets[i].href || '';
      if (href.indexOf('ss-quick-add-feedback.css') !== -1 && sheets[i].media && sheets[i].media.mediaText !== 'print') return true;
    }
    return false;
  }

  document.addEventListener('sacrasoul:quick-add', function (ev) {
    var origin = ev.detail && ev.detail.origin;
    if (!REDUCED_MOTION && sheetReady()) flyToCart(origin);
    markReopenable(origin);
  });

  /* Re-tapping the just-added control within the window opens the drawer
     instead of adding a second unit or replaying the surface's own click
     handler (collections-page.js, hp-home.js, the browse quiz all attach
     their own bubble-phase listener to the same element). Capture phase
     wins the race and stopImmediatePropagation keeps this a single ending,
     not an add-then-open double fire. */
  document.addEventListener('click', function (e) {
    var marked = e.target.closest ? e.target.closest('.' + MARK_CLASS) : null;
    if (!marked) return;
    e.preventDefault();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    else e.stopPropagation();
    marked.classList.remove(MARK_CLASS);
    if (marked._ssQafTimer) { clearTimeout(marked._ssQafTimer); marked._ssQafTimer = null; }
    if (window.SacrasoulCart && window.SacrasoulCart.open) window.SacrasoulCart.open();
  }, true);
})();
